
import { APIDataRoom } from '@evertel/types'
import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { Api } from '@evertel/api'
import { debounce, uniqBy } from 'lodash'
import { SessionService } from '@evertel/session/SessionService'

/**
 * managed = rooms the current user manages.
 * member = rooms the current user is a member of.
 * public = public/crisis/incident-command rooms.
 * department = all rooms in the department
 */
export type RoomSearchScopeProps = 'managed' | 'member' | 'public' | 'department' | 'forward'

export type RoomType = 'not-archived' | 'archived' | 'both'

class RoomsSearchController {
    searchScope: RoomSearchScopeProps = 'department'
    departmentId = 0
    rooms: APIDataRoom[] = []
    roomsLatestFetch: APIDataRoom[] = []
    roomsCount = 0
    searchTerm = ''
    page = 1
    limit = 100
    infiniteScroll = false
    archivedFilter: RoomType = 'not-archived'
    isLoading = false

    constructor(
        private api: Api,
        private sessionService: SessionService
    ) {
        makeAutoObservable(this)
    }

    init({
        searchScope = this.searchScope,
        departmentId = this.departmentId,
        infiniteScroll = this.infiniteScroll,
        archivedFilter = this.archivedFilter
    }: {
        searchScope: RoomSearchScopeProps
        departmentId: number
        infiniteScroll: boolean
        filterOutArchived?: boolean
        archivedFilter?: RoomType
    }) {
        if (!departmentId && !['public', 'member'].includes(searchScope)) {
            console.error('ERROR: /libs/shared/RoomsSearchController init(): No departmentId and not public or member search')
        }

        this.searchScope = searchScope
        this.infiniteScroll = infiniteScroll
        this.departmentId = departmentId
        this.archivedFilter = archivedFilter
        this.resetSearch()

    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        // null/undefined would be interpolated into the `like` filter as '%null%'
        this.searchTerm = value ?? ''
    }

    setPage(pageNumber: number) {
        this.page = pageNumber
    }

    debouncedSearchInternal = debounce(async () => {
        try {
            await this.search()
        } catch (error) {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }, 300)

    debouncedSearch = () => {
        this.isLoading = true
        return this.debouncedSearchInternal()
    }

    search = async () => {
        if (!this.departmentId && !['public', 'member'].includes(this.searchScope)) {
            console.error('ERROR: /libs/shared/RoomsSearchController search(): No departmentId and not public or member search')
            return
        }

        runInAction(() => {
            this.isLoading = true
            this.roomsLatestFetch = []
        })

        let count = this.roomsCount
        let newRooms: APIDataRoom[] = []

        // grab the total count of rooms for this search (once each new search)
        if (!this.roomsCount) {
            count = await this.fetchRoomsCount()
        }

        // stop if we already fetched them all
        if (this.rooms?.length < count) {

            try {
                if (this.searchScope === 'managed') {
                // search rooms the current user manages
                    const filter = this.filter
                    filter.where['departmentId'] = this.departmentId

                    newRooms = await this.api.Routes.BlueUser.getRoomsManaged(this.sessionService.currentUserId, filter)

                } else if (this.searchScope === 'member') {
                // search rooms the current user is a member OR manager of (includes all agencies by default)
                    newRooms = await this.api.Routes.BlueUser.getRooms(this.sessionService.currentUserId, this.filter)

                } else if (this.searchScope === 'department') {
                // search all rooms in the current department
                    newRooms = await this.api.Routes.Department.getRooms(this.departmentId, this.filter)

                } else if (this.searchScope === 'forward') {
                    const filter = this.filter
                    filter.where['departmentId'] = this.departmentId

                    newRooms = await this.api.Routes.BlueUser.getRoomsForward(this.sessionService.currentUserId, filter)
                } else if (this.searchScope === 'public') {
                // search all public/crisis/incident-command rooms
                    const filter = this.filter
                    filter.where['searchTerm'] = this.searchTerm
                    newRooms = await this.api.Routes.Room.getSearchPublic(filter)
                }

            } catch (e) {
                console.error('ERROR: /libs/shared/RoomsSearchController search()', e)
                throw e
            }
        }

        runInAction(() => {
            this.roomsLatestFetch = newRooms
            this.rooms = (this.infiniteScroll) ? uniqBy([...this.rooms, ...newRooms], 'id') : newRooms

            this.isLoading = false
        })

        // we return the new array as some components only need this.
        // however, you should usually reference 'this.rooms' observable
        return this.roomsLatestFetch

    }

    async fetchRoomsCount() {
        let count = 0

        try {
            if (this.searchScope === 'managed') {
                const where = this.filter.where
                where['departmentId'] = this.departmentId

                count = (await this.api.Routes.BlueUser.getRoomsManagedCount(this.sessionService.currentUserId, where)).count

            } else if (this.searchScope === 'member') {
                count = (await this.api.Routes.BlueUser.getRoomsCount(this.sessionService.currentUserId, this.filter.where)).count

            } else if (this.searchScope === 'department') {
                count = (await this.api.Routes.Department.getRoomsCount(this.departmentId, this.filter.where)).count

            } else if (this.searchScope === 'forward') {
                const where = this.filter.where
                where['departmentId'] = this.departmentId

                count = (await this.api.Routes.BlueUser.getRoomsForwardCount(this.sessionService.currentUserId, where))?.count

            } else if (this.searchScope === 'public') {
                // public rooms
                const where = this.filter.where
                where['searchTerm'] = this.searchTerm
                count = (await this.api.Routes.Room.getSearchPublicCount(where)).count
            }

        } catch (e) {
            console.error('ERROR: /libs/shared/RoomsSearchController fetchRoomsCount()', e)
            throw e
        }

        runInAction(() => {
            this.roomsCount = count ?? 0
        })

        return this.roomsCount
    }

    loadNextPage(): void {
        const maxPage = Math.ceil(this.roomsCount / this.limit)
        if (this.page >= maxPage) return
        this.page = this.page + 1
        this.search()
    }

    resetSearchMeta(): void {
        this.page = 1
        this.rooms = []
        this.roomsCount = 0
    }

    resetSearch(): void {
        this.resetSearchMeta()
        this.searchTerm = ''
    }

    get filter() {
        const whereFilter = [
            {
                name: {
                    like: `%${this.searchTerm}%`
                }
            }
        ] as any

        if (this.archivedFilter !== 'both') {
            whereFilter.push({
                isArchived: this.archivedFilter === 'archived'
            })
        }

        return {
            where: {
                and: whereFilter
            },
            //fields: ['id', 'name', 'departmentId'],
            skip: (this.page - 1) * this.limit,
            limit: this.limit,
            order: 'name ASC'
        }
    }
}

decorate(injectable(), RoomsSearchController)
decorate(inject(Api), RoomsSearchController, 0)
decorate(inject(SessionService), RoomsSearchController, 1)

export { RoomsSearchController }
